import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export function useUpdateParsedFields() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async ({
      billId,
      updates,
    }: {
      billId: string;
      updates: Record<string, any>;
    }) => {
      const { data, error } = await supabase.functions.invoke('parser-data-update', {
        body: {
          action: 'update_parsed_fields',
          bill_id: billId,
          updates,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill-details', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['collection-list', currentWorkspace?.id] });
      toast({
        title: 'Fields updated',
        description: 'Parsed data updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Update failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useApproveBill() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async (billId: string) => {
      const { data, error } = await supabase.functions.invoke('parser-data-update', {
        body: {
          action: 'approve_bill',
          bill_id: billId,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill-details', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['collection-list', currentWorkspace?.id] });
      toast({
        title: 'Bill approved',
        description: 'Bill marked as approved and valid',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Approval failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useRejectBill() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async ({ billId, reason }: { billId: string; reason?: string }) => {
      const { data, error } = await supabase.functions.invoke('parser-data-update', {
        body: {
          action: 'reject_bill',
          bill_id: billId,
          reason,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bill-details', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['collection-list', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['parser-jobs', currentWorkspace?.id] });
      toast({
        title: 'Bill rejected',
        description: 'Bill sent back for re-review',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Rejection failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
